import { Box, Button, DestructiveModal, Text } from '@modusign/design-system';
import { useFields } from '@modusign/react-signing';
import { Locale, SignerToReference, SigningMethod } from '@modusign/react-signing/types';
import { produce } from 'immer';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import OutsideClickHandler from 'react-outside-click-handler';
import { useUpdateEffect } from 'react-use';

import { NavigationButtons, XButton } from '@app/components/Buttons';
import SignerFormNameField from '@app/components/SignerFormNameField';
import SignerFormRoleField from '@app/components/SignerFormRoleField';
import SignerLanguageSelect from '@app/components/SignerLanguageSelect';
import { AddressBookContact } from '@app/features/addressBook';
import SignerFormContactAndMethodField from '@app/features/apply-react-signing/components/SignerCardUI/SignerFormContactAndMethodField';
import createOrdinalCharacter, { AllowedOrdinalNumbers } from '@app/libs/ordinalCharacter';

interface TemplateSignerCardProps {
  id: string;
  indexNumber: number;
  style?: CSSProperties;
  isInOrder: boolean | null;
  onSignerRemove?: (id: string) => void;
  onSequenceChange: (srcIndex: number, destIndex: number) => void;
}

const defaultProps = {
  style: {},
};

function TemplateSignerCard({
  id,
  indexNumber,
  style,
  isInOrder,
  onSignerRemove,
  onSequenceChange,
}: TemplateSignerCardProps) {
  const [addressBookContact, setAddressBookContact] = useState<AddressBookContact | null>(null);

  const { control, getValues, register, reset, setValue, trigger, watch } =
    useFormContext<Record<string, SignerToReference>>();
  const formValues = watch(id);
  const name = formValues?.name;
  const contact = formValues?.contact;

  const [predefined, setPredefined] = useState(!!(name || contact));
  const [isSignerRemoveWarningModalOpen, setIsSignerRemoveWarningModalOpen] = useState(false);

  const { fields } = useFields();

  const getSigningMethodOnSelectAddressBookSelect = useCallback(
    (addressBookContact: AddressBookContact): SigningMethod => {
      const { defaultForwardingMethod } = addressBookContact;
      const { signingMethod } = formValues;

      if (signingMethod !== 'IN_PERSON') {
        return defaultForwardingMethod === 'EMAIL' ? 'EMAIL' : 'KAKAO';
      }

      return signingMethod;
    },
    [formValues]
  );

  const getContactOnSelectAddressBookSelect = useCallback(
    (addressBookContact: AddressBookContact): string | undefined => {
      const { defaultForwardingMethod, email, phoneNumber } = addressBookContact;
      const { contact } = formValues;

      if (defaultForwardingMethod === 'EMAIL' && email) {
        return email;
      }
      if (defaultForwardingMethod === 'KAKAO' && phoneNumber) {
        return phoneNumber;
      }

      return contact;
    },
    [formValues]
  );

  useEffect(() => {
    register(`${id}.order`);
    register(`${id}.index`);
    // setValue의 name key를 nested object의 특정 property로 하면
    // 두번째 인자인 value도 특정 property value type으로 추론되어야하는데,
    // form 전체 타입으로 추론되고 있어 타입을 무시합니다.
    // dynamic property key 일 때 타입추론에 문제가 있는 것으로 보입니다.
    // @see https://react-hook-form.com/v6/api#setValue
    setValue(
      `${id}.order`,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      isInOrder ? indexNumber + 1 : 1,
      { shouldValidate: true }
    );

    setValue(
      `${id}.index`,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      indexNumber,
      { shouldValidate: true }
    );
  }, [indexNumber, isInOrder, id, register, setValue]);

  useUpdateEffect(() => {
    trigger();
  }, [isInOrder]);

  const setFormDefaultValues = useCallback(
    ({
      name,
      contact,
      signingMethod,
      locale,
    }: {
      name?: string;
      contact?: string;
      signingMethod?: SigningMethod | null;
      locale?: Locale;
    }) => {
      // reset은 defaultValues를 업데이트합니다. undefined일 경우 기존 값을 유지합니다.
      reset(
        produce(getValues(), (draft) => {
          draft[id].name = name ?? draft[id].name;
          draft[id].contact = contact ?? draft[id].contact;
          draft[id].signingMethod =
            signingMethod === undefined ? draft[id].signingMethod : signingMethod;
          draft[id].locale = locale ?? draft[id].locale;
        }),
        { errors: true }
      );
    },
    [getValues, id, reset]
  );

  const handleOutsideClick = useCallback(() => {
    if (!contact && !name) {
      // 이름과 연락처가 모두 없을 경우 불러온 주소록, 이름, 연락처, 서명 타입의 초기값을 복구시켜준다.
      setPredefined(false);
      setAddressBookContact(null);
      setFormDefaultValues({
        name: '',
        contact: '',
        signingMethod: null,
      });
    } else if (!contact && formValues.signingMethod !== null) {
      // 이름만 입력이 안된경우 주소록, 서명 타입의 초기값을 복구시켜준다.
      setAddressBookContact(null);
      setFormDefaultValues({
        signingMethod: null,
      });
    }
  }, [formValues, contact, name, setFormDefaultValues]);

  return (
    <Box
      data-testid="templateParticipantCard"
      id={indexNumber === 0 ? 'first-participant' : undefined}
      position="relative"
      style={style}
    >
      <Box
        className="Card Card--theme-editor"
        p={6}
        sx={{
          cursor: 'move',
        }}
      >
        <Box mb={4}>
          <Text fontWeight="medium">
            <Text as="p" display={isInOrder ? 'inline-block' : 'none'}>
              {createOrdinalCharacter((indexNumber + 1) as AllowedOrdinalNumbers)}
            </Text>{' '}
            <Text as="p" display="inline-block">
              서명자
            </Text>
          </Text>
        </Box>
        <XButton
          aria-label="delete signer"
          disabled={!onSignerRemove}
          sx={{ position: 'absolute', right: '10px', top: '10px' }}
          onClick={() => {
            const shouldWarnRemovingField = fields.some(
              (field) => field.participantReference.id === id
            );

            if (shouldWarnRemovingField) {
              setIsSignerRemoveWarningModalOpen(true);
            } else {
              onSignerRemove?.(id);
            }
          }}
        />
        <SignerFormRoleField signerId={id} />
        {!predefined && (
          <Button weight="light" width="100%" onClick={() => setPredefined(true)}>
            + 미리 정보 입력하기
          </Button>
        )}
        {predefined && (
          <OutsideClickHandler onOutsideClick={handleOutsideClick}>
            <SignerFormNameField
              required
              signerId={id}
              onAddressBookContactSelect={(contactData) => {
                setAddressBookContact(contactData);
                reset({
                  ...getValues(),
                  [id]: {
                    ...formValues,
                    name: contactData.name,
                    contact: getContactOnSelectAddressBookSelect(contactData),
                    signingMethod: getSigningMethodOnSelectAddressBookSelect(contactData),
                  },
                });
              }}
            />
            <Controller
              control={control}
              defaultValue={null}
              name={`${id}.signingMethod`}
              render={({ value: signingMethod, onChange: onMethodChange }) => (
                <SignerFormContactAndMethodField
                  addressBookContact={addressBookContact}
                  order={isInOrder ? indexNumber + 1 : 1}
                  required
                  selectableMethods={['EMAIL', 'KAKAO', 'IN_PERSON']}
                  signerId={id}
                  type={signingMethod}
                  onSigningMethodChange={onMethodChange}
                />
              )}
              shouldUnregister
            />
          </OutsideClickHandler>
        )}
        <Box
          mt={
            formValues?.signingMethod === 'KAKAO' || formValues?.signingMethod === 'EMAIL' ? 0 : 4
          }
        >
          <SignerLanguageSelect signerId={id} />
        </Box>
      </Box>
      <NavigationButtons
        style={{ position: 'absolute', right: -46, top: predefined ? 35 : 17 }}
        onButtonBottomClick={() => onSequenceChange(indexNumber, 1)}
        onButtonTopClick={() => onSequenceChange(indexNumber, -1)}
      />

      <DestructiveModal
        closeButtonLabel="취소"
        confirmButtonLabel="삭제"
        isOpen={isSignerRemoveWarningModalOpen}
        title="서명자 삭제 안내"
        width="xs"
        onClose={() => setIsSignerRemoveWarningModalOpen(false)}
        onConfirm={() => {
          setIsSignerRemoveWarningModalOpen(false);
          onSignerRemove?.(id);
        }}
      >
        <Text textAlign="center">서명자를 삭제하면 설정했던 입력란도 함께 삭제됩니다.</Text>
      </DestructiveModal>
    </Box>
  );
}

TemplateSignerCard.defaultProps = defaultProps;

export default TemplateSignerCard;
